import { ICalendarEventDetailsWidgetStyle } from "@pageBuilder/typings";
import { CssProperties } from "src/models/css-property";
import { ElementStyleService } from "src/services/element-style.service";

export class CalendarEventDetailsWidgetStyleRenderer {

    constructor(private readonly styleService: ElementStyleService) {
    }

    public render(element: HTMLElement, style: ICalendarEventDetailsWidgetStyle): void {
        this.renderLayoutStyles(element, style);
        this.renderBackgroundStyles(element, style);
        this.renderHeadingStyles(element, style);
        this.renderTextStyles(element, style);
        this.renderButtonStyles(element, style);
    }

    public getContentSelectors(element: HTMLElement): string[] {
        return [
            this.layoutSelector(element.id),
            this.backgroundSelector(element.id),
            this.headingSelector(element.id),
            this.textSelector(element.id),
            this.buttonSelector(element.id),
            this.buttonHoverSelector(element.id),
        ];
    }

    private renderLayoutStyles(element: HTMLElement, style: ICalendarEventDetailsWidgetStyle): void {
        const styleProps = new CssProperties();

        styleProps.tryAdd("font-family", style.font.family);
        styleProps.tryAdd("padding", `${style.padding + style.borderThickness}px`);

        this.styleService.setStyle(element, this.layoutSelector(element.id), styleProps.data);
    }

    private renderBackgroundStyles(element: HTMLElement, style: ICalendarEventDetailsWidgetStyle): void {
        const styleProps = new CssProperties();

        if (style.borderRadius) {
            styleProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            styleProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            styleProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            styleProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        styleProps.tryAdd("background-color", style.background.color);
        styleProps.tryAdd("border", `${style.borderThickness}px solid`);
        styleProps.tryAdd("border-color", style.borderColor);

        this.styleService.setStyle(element, this.backgroundSelector(element.id), styleProps.data);
    }

    private renderHeadingStyles(element: HTMLElement, style: ICalendarEventDetailsWidgetStyle): void {
        const styleProps = new CssProperties();

        styleProps.tryAdd("font-weight", style.headingFont?.weight);
        styleProps.tryAdd("font-size", style.headingFont?.size);
        styleProps.tryAdd("color", style.headingFont?.color);

        this.styleService.setStyle(element, this.headingSelector(element.id), styleProps.data);
    }

    private renderTextStyles(element: HTMLElement, style: ICalendarEventDetailsWidgetStyle): void {
        const styleProps = new CssProperties();

        styleProps.tryAdd("font-weight", style.textFont?.weight);
        styleProps.tryAdd("font-size", style.textFont?.size);
        styleProps.tryAdd("color", style.textFont?.color);

        this.styleService.setStyle(element, this.textSelector(element.id), styleProps.data);
    }

    private renderButtonStyles(element: HTMLElement, style: ICalendarEventDetailsWidgetStyle): void {
        const styleProps = new CssProperties();
        const hoverStyleProps = new CssProperties();

        if (style.button.borderRadius) {
            styleProps.tryAdd("border-top-left-radius", `${style.button.borderRadius.topLeftPx}px`);
            styleProps.tryAdd("border-top-right-radius", `${style.button.borderRadius.topRightPx}px`);
            styleProps.tryAdd("border-bottom-left-radius", `${style.button.borderRadius.bottomLeftPx}px`);
            styleProps.tryAdd("border-bottom-right-radius", `${style.button.borderRadius.bottomRightPx}px`);
        }

        styleProps.tryAdd("font-size", style.button.font?.size);
        styleProps.tryAdd("border", `${style.button.thickness} solid`);
        styleProps.tryAdd("border-color", style.button.borderColor);

        styleProps.tryAdd("background-color", style.button.backgroundColor);
        hoverStyleProps.tryAdd("background-color", style.button.backgroundHoverColor);

        styleProps.tryAdd("border-color", style.button.borderColor);
        hoverStyleProps.tryAdd("border-color", style.button.borderHoverColor);

        styleProps.tryAdd("color", style.button.color);
        hoverStyleProps.tryAdd("color", style.button.hoverColor);

        this.styleService.setStyle(element, this.buttonSelector(element.id), styleProps.data);
        this.styleService.setStyle(element, this.buttonHoverSelector(element.id), hoverStyleProps.data);
    }

    private layoutSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout`;
    }

    private backgroundSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .background`;
    }

    private headingSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .title-wrapper .title, ` +
               `#${elementId} .widget-content .layout .warning-wrapper .title`;
    }

    private textSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .details-wrapper span, ` +
               `#${elementId} .widget-content .layout .warning-wrapper .info`;
    }

    private buttonSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .register-btn`;
    }

    private buttonHoverSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .register-btn:hover`;
    }

}
